import type {
  Audience,
  CanonicalDocument,
  Commentary,
  CommentaryKind,
  DocDocument,
  DocNode,
  FeatureFlags,
  Genre,
  ImportDocument,
  InlineLine,
  NodeType,
  Role,
} from '@cca/corpus-shared';

import type { AuditSession } from '@cca/ui';
import { reportTelemetry } from '@cca/ui';
import type { DocxParagraph } from '../import/docxParse';

export type { CanonicalDocument, DocDocument } from '@cca/corpus-shared';

/** Fields for creating a liturgy block (node). */
export interface NewNodeInput {
  type: NodeType;
  parentId: string | null;
  orderIndex: number;
  lines: InlineLine[];
  label?: string | null;
}
export interface NodePatchInput {
  type?: NodeType;
  lines?: InlineLine[];
  label?: string | null;
  parentId?: string | null;
  orderIndex?: number;
}

/** Fields the Commission supplies when creating a note (or thread reply). */
export interface NewCommentaryInput {
  targetNodeId: string;
  kind: CommentaryKind;
  audience: Audience;
  body: string;
  span?: string | null;
  relatesTo?: string | null;
}
export interface CommentaryPatchInput {
  body?: string;
  kind?: CommentaryKind;
  audience?: Audience;
  span?: string | null;
  targetNodeId?: string;
}

/** Non-sensitive instance identity (GET /api/instance) — drives header + theme,
 *  and carries the feature flags so the SPA can hide a switched-off feature. */
export interface InstanceInfo {
  instanceName: string;
  genre: Genre;
  theme: string;
  features: FeatureFlags;
}

/** Current session identity + resolved tier (GET /api/auth/me). `isAdmin` is the
 *  registrar — drives the feature-flag admin control. */
export interface SessionInfo {
  email: string;
  tier: Role;
  isAdmin: boolean;
}

/**
 * One sign-in in the registrar's login audit trail (GET /api/auth/login-history).
 * Shape matches the shared @cca/ui audit table (AuditSession).
 */
export type LoginSession = AuditSession;

/** One entry in a document's version history (metadata only — no snapshot blob). */
export interface RevisionMeta {
  id: string;
  label: string | null;
  author: string | null;
  createdAt: string;
  /** Diff-lite "what changed" line vs the previous revision; null for the first / pre-feature. */
  changeSummary: string | null;
}

/** A single revision with its full snapshot — for the visual diff (Commission only). */
export interface RevisionDetail {
  meta: RevisionMeta;
  snapshot: CanonicalDocument;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = 'ApiError';
    // Telemetry (Phase 4): surface server errors even when the app catches them.
    // 4xx (auth/validation) are expected, so only 5xx is reported.
    if (status >= 500) reportTelemetry('api_error', message);
  }
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'include' });
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new ApiError(
      `GET ${path} failed: HTTP ${res.status}${json.error ? ` (${String(json.error)})` : ''}`,
      res.status,
    );
  }
  return json as T;
}

async function mutate<T>(
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      // CSRF defence-in-depth — the Worker rejects mutating requests without it.
      'X-Requested-With': 'XMLHttpRequest',
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new ApiError(
      `${method} ${path} failed: HTTP ${res.status}${json.error ? ` (${String(json.error)})` : ''}`,
      res.status,
    );
  }
  return json as T;
}
const post = <T>(path: string, body: unknown) => mutate<T>('POST', path, body);

export const api = {
  instance: () => get<InstanceInfo>('/api/instance'),
  me: () => get<SessionInfo>('/api/auth/me'),
  // The sign-in ceremony (OTP + passkey request/verify) is owned by the shared
  // @cca/ui <LoginScreen> (ADR-018), which talks to the worker directly with its
  // own fetch — so those methods no longer live on this app client.
  logout: () => post<{ ok: true }>('/api/auth/logout', {}),
  /** "Sign out all devices" — ends every session for the caller's email. */
  logoutAll: () => post<{ ok: true }>('/api/auth/logout-all', {}),
  // Registrar-only sign-in audit trail.
  loginHistory: () => get<{ sessions: LoginSession[] }>('/api/auth/login-history'),
  documents: () => get<{ documents: DocDocument[] }>('/api/documents'),
  // Commentary is already audience-filtered server-side (the non-negotiable);
  // the reading view just presents what arrives.
  document: (id: string) => get<CanonicalDocument>(`/api/documents/${encodeURIComponent(id)}`),

  // Commentary authoring (Commission only — the Worker enforces canWrite).
  createCommentary: (docId: string, input: NewCommentaryInput) =>
    mutate<Commentary>('POST', `/api/documents/${encodeURIComponent(docId)}/commentary`, input),
  updateCommentary: (docId: string, cid: string, patch: CommentaryPatchInput) =>
    mutate<Commentary>(
      'PATCH',
      `/api/documents/${encodeURIComponent(docId)}/commentary/${encodeURIComponent(cid)}`,
      patch,
    ),
  deleteCommentary: (docId: string, cid: string) =>
    mutate<{ ok: true }>(
      'DELETE',
      `/api/documents/${encodeURIComponent(docId)}/commentary/${encodeURIComponent(cid)}`,
    ),
  // Replace the set of ADDITIONAL blocks a note is pinned to (multi-anchor) —
  // Commission only. Returns the note with its refreshed extraTargets.
  setCommentaryAnchors: (docId: string, cid: string, nodeIds: string[]) =>
    mutate<Commentary>(
      'PUT',
      `/api/documents/${encodeURIComponent(docId)}/commentary/${encodeURIComponent(cid)}/anchors`,
      { nodeIds },
    ),

  // Liturgy block (node) authoring (Commission only).
  createNode: (docId: string, input: NewNodeInput) =>
    mutate<DocNode>('POST', `/api/documents/${encodeURIComponent(docId)}/nodes`, input),
  updateNode: (docId: string, nid: string, patch: NodePatchInput) =>
    mutate<DocNode>(
      'PATCH',
      `/api/documents/${encodeURIComponent(docId)}/nodes/${encodeURIComponent(nid)}`,
      patch,
    ),
  deleteNode: (docId: string, nid: string) =>
    mutate<{ ok: true }>(
      'DELETE',
      `/api/documents/${encodeURIComponent(docId)}/nodes/${encodeURIComponent(nid)}`,
    ),
  restoreNode: (docId: string, nid: string) =>
    mutate<{ ok: true }>(
      'POST',
      `/api/documents/${encodeURIComponent(docId)}/nodes/${encodeURIComponent(nid)}/restore`,
    ),

  // Bulk import a parsed document (.docx) — Commission only. Returns the new id.
  importDocument: (payload: ImportDocument) =>
    mutate<{ id: string }>('POST', '/api/documents/import', payload),

  // Parse a linked Google Doc → paragraphs + footnotes (the .docx-parser shape),
  // for the shared mapper to preview before import. One-time seed. Commission only.
  parseGdoc: (url: string) =>
    mutate<{ paragraphs: DocxParagraph[]; footnotes: Record<string, string> }>(
      'POST',
      '/api/documents/import/gdoc-parse',
      { url },
    ),

  // Duplicate a document into a new one — Commission only. `includeCommentary`
  // carries the rationale sticky notes across; omit/false for a clean copy.
  // Returns the new document id.
  duplicateDocument: (id: string, includeCommentary: boolean, title?: string) =>
    mutate<{ id: string }>('POST', `/api/documents/${encodeURIComponent(id)}/duplicate`, {
      includeCommentary,
      ...(title ? { title } : {}),
    }),

  // Permanently delete a document and all its content — Commission only.
  deleteDocument: (id: string) =>
    mutate<{ ok: true }>('DELETE', `/api/documents/${encodeURIComponent(id)}`),

  // Replace a document's free-text working notes (editor scratchpad) — Commission
  // only. Inline bold/italic only; stored as InlineLine[].
  updateDocumentNotes: (id: string, notes: InlineLine[]) =>
    mutate<{ ok: true }>('PATCH', `/api/documents/${encodeURIComponent(id)}`, { notes }),

  // Rename a document — Commission only.
  renameDocument: (id: string, title: string) =>
    mutate<{ ok: true }>('PATCH', `/api/documents/${encodeURIComponent(id)}`, { title }),

  // Version history (Commission only). Auto-snapshots accrue as the document is
  // edited; `saveRevision` adds an explicit named checkpoint; `restoreRevision`
  // rolls the whole document back (ID-preserving, so commentary stays anchored).
  revisions: (id: string) =>
    get<{ revisions: RevisionMeta[] }>(`/api/documents/${encodeURIComponent(id)}/revisions`),
  saveRevision: (id: string, label?: string) =>
    mutate<{ id: string | null }>('POST', `/api/documents/${encodeURIComponent(id)}/revisions`, {
      ...(label ? { label } : {}),
    }),
  restoreRevision: (id: string, revisionId: string) =>
    mutate<{ ok: true }>(
      'POST',
      `/api/documents/${encodeURIComponent(id)}/revisions/${encodeURIComponent(revisionId)}/restore`,
    ),
  // One revision's full snapshot, for the Compare (visual diff) view — Commission only.
  getRevision: (id: string, revisionId: string) =>
    get<RevisionDetail>(
      `/api/documents/${encodeURIComponent(id)}/revisions/${encodeURIComponent(revisionId)}`,
    ),

  // Feature flags — registrar-only. Current values for the admin panel, and a
  // single-flag toggle. (The public read of flag values is on /api/instance.)
  flags: () => get<{ flags: FeatureFlags }>('/api/flags'),
  setFlag: (flag: keyof FeatureFlags, value: boolean) =>
    mutate<{ flags: FeatureFlags }>('POST', '/api/flags', { flag, value }),
};
